/*
 * Project: bellcloud
 * File: apps/frontend/src/components/ui/common/PhoneInput/utils.ts
 * Description: Utility functions for phone number handling
 */

import { defaultCountries, parseCountry } from 'react-international-phone';
import type { E164PhoneNumber, PhoneData, PhoneValidation } from './types';

// 유효한 국가 dial code Set (3자리 → 2자리 → 1자리 우선순위로 분리)
const DIAL_CODE_SET = new Set(defaultCountries.map(c => parseCountry(c).dialCode));

/**
 * E.164 형식 검증
 * 형식: +[1-9][0-9]{1,14}
 * 최소 길이: 3자리 (+와 국가코드 최소 1자리 + 번호 최소 1자리)
 * 최대 길이: 16자리 (+와 숫자 포함)
 */
export const validateE164 = (phone: string): boolean => {
  const e164Regex = /^\+[1-9]\d{1,14}$/;
  return e164Regex.test(phone);
};

/**
 * 국제 전화번호에서 트렁크 프리픽스(trunk prefix) 제거
 *
 * 대부분의 국가는 국내 전화 시 앞에 0을 붙이지만(예: 010-xxxx)
 * 국제 전화 형식(E.164)에서는 이 0을 제거해야 합니다.
 *
 * 예외: 일부 국가는 국제 형식에서도 0을 유지합니다
 *   - 이탈리아(+39): 고정전화는 0 유지 (예: +39 06 xxxx)
 *   - 러시아(+7): 트렁크 프리픽스 없음
 *   - 미국/캐나다(+1): 트렁크 프리픽스 없음
 *   - 중국(+86): 트렁크 프리픽스 없음
 *   - 스페인(+34): 트렁크 프리픽스 없음
 *
 * 0을 제거해야 하는 주요 국가 목록:
 */
const STRIP_TRUNK_PREFIXES: string[] = [
  '+82',  // 한국
  '+44',  // 영국
  '+49',  // 독일
  '+33',  // 프랑스
  '+61',  // 호주
  '+31',  // 네덜란드
  '+32',  // 벨기에
  '+81',  // 일본
  '+91',  // 인도
  '+62',  // 인도네시아
  '+63',  // 필리핀
  '+64',  // 뉴질랜드
  '+66',  // 태국
  '+84',  // 베트남
  '+90',  // 터키
  '+92',  // 파키스탄
  '+94',  // 스리랑카
  '+98',  // 이란
  '+212', // 모로코
  '+213', // 알제리
  '+216', // 튀니지
  '+234', // 나이지리아
  '+254', // 케냐
  '+256', // 우간다
  '+27',  // 남아프리카
  '+351', // 포르투갈
  '+353', // 아일랜드
  '+354', // 아이슬란드
  '+358', // 핀란드
  '+43',  // 오스트리아
  '+41',  // 스위스
  '+48',  // 폴란드
  '+420', // 체코
  '+36',  // 헝가리
  '+40',  // 루마니아
  '+380', // 우크라이나
];

export const normalizePhone = (phone: string): string => {
  if (!phone) return phone;

  for (const prefix of STRIP_TRUNK_PREFIXES) {
    // 국가코드 바로 뒤에 0이 붙어 있으면 제거
    if (phone.startsWith(prefix + '0')) {
      return prefix + phone.slice(prefix.length + 1);
    }
  }

  return phone;
};

/**
 * 전화번호를 화면 표시용 형식으로 변환
 * 정규화 후 국가코드 + 공백 + 나머지 번호
 *
 * 예: +8201098420917 → +82 1098420917  (정규화 후: +82 1098420917)
 * 예: +821098420917  → +82 1098420917
 * 예: +12125551234   → +1 2125551234
 * 예: 빈 값          → '-'
 */
export const formatPhoneForDisplay = (phone: string | null | undefined): string => {
  if (!phone) return '-';

  const normalized = normalizePhone(phone);

  if (!normalized.startsWith('+')) return phone;

  const digits = normalized.slice(1); // '+' 제거

  // 실제 국가코드 목록으로 longest-prefix 매칭 (3 → 2 → 1자리 순서)
  // greedy 정규식 대신 사용: +821012345678에서 '821'을 국가코드로 잡는 문제 방지
  for (const len of [3, 2, 1]) {
    if (digits.length > len) {
      const dialCode = digits.slice(0, len);
      if (DIAL_CODE_SET.has(dialCode)) {
        return `+${dialCode} ${digits.slice(len)}`;
      }
    }
  }

  return normalized;
};

/**
 * 전화번호 유효성 검사 (상세)
 */
export const validatePhone = (phone: string, required: boolean = false): PhoneValidation => {
  // 빈 값 체크
  if (!phone || phone.trim() === '') {
    if (required) {
      return {
        isValid: false,
        error: 'phone_required',
      };
    }
    return { isValid: true };
  }

  // E.164 형식 검증
  if (!validateE164(phone)) {
    return {
      isValid: false,
      error: 'phone_invalid',
    };
  }

  return { isValid: true };
};

/**
 * 국가 코드 추출
 * 예: +821012345678 -> +82
 */
export const extractCountryCode = (phone: E164PhoneNumber): string | null => {
  if (!phone || !phone.startsWith('+')) {
    return null;
  }

  // 국가 코드는 1~3자리
  const match = phone.match(/^\+(\d{1,3})/);
  return match ? `+${match[1]}` : null;
};

/**
 * 전화번호에서 숫자만 추출
 */
export const extractDigits = (phone: string): string => {
  return phone.replace(/\D/g, '');
};

/**
 * 로컬 번호를 E.164 형식으로 변환
 * 예: 010-1234-5678 (한국) -> +821012345678
 */
export const toE164 = (
  localNumber: string,
  countryCode: string = '+82'
): E164PhoneNumber | null => {
  // 숫자만 추출
  const digits = extractDigits(localNumber);

  if (!digits) {
    return null;
  }

  // 한국 번호의 경우 앞의 0 제거
  let cleanedDigits = digits;
  if (countryCode === '+82' && digits.startsWith('0')) {
    cleanedDigits = digits.substring(1);
  }

  const e164 = `${countryCode}${cleanedDigits}`;

  // 유효성 검증
  return validateE164(e164) ? e164 : null;
};

/**
 * E.164 형식을 로컬 형식으로 변환
 * 예: +821012345678 -> 010-1234-5678 (한국)
 */
export const toLocalFormat = (phone: E164PhoneNumber, countryCode?: string): string => {
  if (!phone || !phone.startsWith('+')) {
    return phone;
  }

  const detectedCountryCode = countryCode || extractCountryCode(phone);

  // 국가 코드 제거
  const numberWithoutCountry = phone.replace(/^\+\d{1,3}/, '');

  // 한국 번호 포맷팅
  if (detectedCountryCode === '+82' || detectedCountryCode === '82') {
    // 휴대폰: 010-1234-5678
    if (numberWithoutCountry.startsWith('10')) {
      return `0${numberWithoutCountry.slice(0, 2)}-${numberWithoutCountry.slice(2, 6)}-${numberWithoutCountry.slice(6)}`;
    }
    // 서울 지역번호: 02-1234-5678
    if (numberWithoutCountry.startsWith('2')) {
      return `0${numberWithoutCountry.slice(0, 1)}-${numberWithoutCountry.slice(1, 5)}-${numberWithoutCountry.slice(5)}`;
    }
    // 기타 지역번호: 031-123-4567
    return `0${numberWithoutCountry.slice(0, 2)}-${numberWithoutCountry.slice(2, 5)}-${numberWithoutCountry.slice(5)}`;
  }

  // 미국 번호 포맷팅: (212) 555-1234
  if (detectedCountryCode === '+1' || detectedCountryCode === '1') {
    if (numberWithoutCountry.length === 10) {
      return `(${numberWithoutCountry.slice(0, 3)}) ${numberWithoutCountry.slice(3, 6)}-${numberWithoutCountry.slice(6)}`;
    }
  }

  // 기본: 숫자만 반환
  return numberWithoutCountry;
};

/**
 * 국제 형식으로 변환 (공백 포함)
 * 예: +821012345678 -> +82 10 1234 5678
 */
export const toInternationalFormat = (phone: E164PhoneNumber): string => {
  if (!phone || !phone.startsWith('+')) {
    return phone;
  }

  const countryCode = extractCountryCode(phone);
  if (!countryCode) {
    return phone;
  }

  const numberWithoutCountry = phone.replace(countryCode, '');

  // 4자리씩 공백으로 구분
  const formatted = numberWithoutCountry.match(/.{1,4}/g)?.join(' ') || numberWithoutCountry;

  return `${countryCode} ${formatted}`;
};

/**
 * 전화번호 파싱
 */
export const parsePhone = (phone: string, defaultCountry: string = 'kr'): PhoneData | null => {
  if (!phone) {
    return null;
  }

  const isValid = validateE164(phone);
  const countryCode = extractCountryCode(phone);

  return {
    phone: phone as E164PhoneNumber,
    countryCode: countryCode?.replace('+', '') || defaultCountry,
    isValid,
  };
};

/**
 * 전화번호 마스킹 (개인정보 보호)
 * 예: +821012345678 -> +82 10-****-5678
 */
export const maskPhone = (phone: E164PhoneNumber, maskChar: string = '*'): string => {
  if (!phone || phone.length < 8) {
    return phone;
  }

  const countryCode = extractCountryCode(phone);
  const numberWithoutCountry = phone.replace(countryCode || '', '');

  if (numberWithoutCountry.length < 8) {
    return phone;
  }

  // 중간 4자리 마스킹
  const start = numberWithoutCountry.slice(0, 2);
  const masked = maskChar.repeat(4);
  const end = numberWithoutCountry.slice(-4);

  return `${countryCode || ''} ${start}-${masked}-${end}`;
};

/**
 * 국가별 힌트 메시지 반환
 */
export const getPhoneHint = (countryCode: string): string => {
  switch (countryCode.toLowerCase()) {
    case 'kr':
    case '+82':
    case '82':
      return 'phone_hint_kr';
    case 'us':
    case '+1':
    case '1':
      return 'phone_hint_us';
    default:
      return 'phone_hint';
  }
};

/**
 * 전화번호 비교 (E.164 형식 기준)
 */
export const comparePhones = (phone1: string, phone2: string): boolean => {
  if (!phone1 || !phone2) {
    return false;
  }

  // 둘 다 E.164 형식으로 변환 후 비교
  const digits1 = extractDigits(phone1);
  const digits2 = extractDigits(phone2);

  return digits1 === digits2;
};

/**
 * 전화번호 배열 중복 제거
 */
export const uniquePhones = (phones: E164PhoneNumber[]): E164PhoneNumber[] => {
  const seen = new Set<string>();
  return phones.filter(phone => {
    const digits = extractDigits(phone);
    if (seen.has(digits)) {
      return false;
    }
    seen.add(digits);
    return true;
  });
};
